import { Fade, IconButton, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { memo } from 'react'

import { SkipReasonCount } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { skipReasonLabel } from '../../helpers/skipReasonLabels'
import { getTranslate } from '../../localization'
import useStyles from './styles/mobile.styles'

/**
 * Skips by reason as label-value cards, for a narrow viewport.
 *
 * WHY A SEPARATE COMPONENT rather than a media query on the grid: at 360px the reason column
 * is left roughly 130px, so a sentence-length reason breaks into ragged fragments under a
 * header that stays aligned. Wrapping stops the overflow, not the unreadability.
 *
 * REFRESH EXISTS AT BOTH BREAKPOINTS. The first version of this MR put the control in the
 * desktop grid's header row only, and a card list has no header row — so under 960px the
 * affordance was simply absent. §2's own reference (ChannelsTableMobile) carries one, which
 * settles the convention; note that it carries `onClick={() => {}}`, a control that is
 * reachable, announced and inert. Not copied: this calls the same refetch the desktop button
 * does, and a test counts the calls.
 *
 * Each figure carries its own label rather than relying on column position — what a card
 * layout costs, and what lets it read correctly without the table roles the grid needs.
 */
interface Props {
  byReason: SkipReasonCount[]
  onRefresh: () => void
}

const SkipReasonListMobile = ({ byReason, onRefresh }: Props) => {
  const classes = useStyles()

  return (
    <div data-cy="SkipReasonsMobile">
      <div className={classes.listHeader}>
        <IconButton
          size="small"
          onClick={onRefresh}
          data-cy="SkipReasonsRefreshMobile"
          aria-label={getTranslate('Refresh')}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </div>
      {byReason.map((row) => (
        <Fade in timeout={600} key={row.reason}>
          <div className={classes.card} data-cy="SkipReasonCardMobile">
            <Typography variant="body2" className={classes.cardLabel}>
              {getTranslate('Reason')}
            </Typography>
            <Typography variant="body1" data-reason={row.reason}>
              {getTranslate(skipReasonLabel(row.reason))}
            </Typography>

            <Typography variant="body2" className={classes.cardLabel}>
              {getTranslate('Skips')}
            </Typography>
            <Typography variant="body1">{formatCount(row.count)}</Typography>
          </div>
        </Fade>
      ))}
    </div>
  )
}

export default memo(SkipReasonListMobile)
